/**
 * Products Table Component
 */

import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { PermissionGate } from "@/components/PermissionGate";
import { Edit, Trash2, Package, Eye } from "lucide-react";
import { Product } from "@/types/product";

interface ProductsTableProps {
  products: Product[];
  onEdit: (product: Product) => void;
  onDelete: (product: Product) => void;
  onView: (product: Product) => void;
  sortField?: string | null;
  sortDirection?: 'asc' | 'desc' | null;
  onSort?: (field: string) => void;
}

export function ProductsTable({ products, onEdit, onDelete, onView, sortField, sortDirection, onSort }: ProductsTableProps) {
  if (products.length === 0) {
    return (
      <div className="border rounded-lg py-12 text-center">
        <Package className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
        <p className="text-muted-foreground">Nenhum produto encontrado</p>
      </div>
    );
  }

  return (
    <div className="border rounded-lg overflow-hidden">
      <div className="overflow-x-auto">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead 
              className="cursor-pointer hover:bg-muted/50 transition-colors"
              onClick={() => onSort?.('nome')}
            >
              Nome
            </TableHead>
            <TableHead 
              className="cursor-pointer hover:bg-muted/50 transition-colors"
              onClick={() => onSort?.('categoria')}
            >
              Categoria
            </TableHead>
            <TableHead className="min-w-[100px]">Unid. Padrão</TableHead>
            <TableHead className="text-right">Ações</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {products.map((product) => (
            <TableRow key={product.id_produto}>
              <TableCell className="font-medium">{product.nome}</TableCell>
              <TableCell>
                <span className="text-sm text-muted-foreground">
                  {product.categoria?.nome || "-"}
                </span>
              </TableCell>
              <TableCell className="min-w-[100px]">
                <span className="text-sm text-muted-foreground">
                  {product.unidade_padrao === 'peso_g' && 'Peso (g)'}
                  {product.unidade_padrao === 'volume_ml' && 'Volume (ml)'}
                  {product.unidade_padrao === 'unidade' && 'Unidade'}
                  {!['peso_g', 'volume_ml', 'unidade'].includes(product.unidade_padrao) && (product.unidade_padrao || '-')}
                </span>
              </TableCell>
              <TableCell className="text-right">
                <div className="flex justify-end gap-2">
                  <PermissionGate resource="products" action="view">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-10 w-10 md:h-9 md:w-9 touch-manipulation"
                      onClick={() => onView(product)}
                      title="Visualizar produto"
                    >
                      <Eye className="w-5 h-5 md:w-4 md:h-4" />
                    </Button>
                  </PermissionGate>
                  <PermissionGate resource="products" action="edit">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-10 w-10 md:h-9 md:w-9 touch-manipulation"
                      onClick={() => onEdit(product)}
                      title="Editar produto"
                    >
                      <Edit className="w-5 h-5 md:w-4 md:h-4" />
                    </Button>
                  </PermissionGate>
                  <PermissionGate resource="products" action="delete">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-10 w-10 md:h-9 md:w-9 touch-manipulation text-destructive hover:text-destructive hover:bg-destructive/10"
                      onClick={() => onDelete(product)}
                      title="Excluir produto"
                    >
                      <Trash2 className="w-5 h-5 md:w-4 md:h-4" />
                    </Button>
                  </PermissionGate>
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      </div>
    </div>
  );
}
